import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import { PenTool, Globe2, Cloud } from 'lucide-react';
import { trackCtaClick } from '../analytics/ctaGoal';
import '../App.css';

type ProductShotProps = {
  /** basename under /product; the phone crop is the same name with -mobile */
  name: string;
  alt: string;
  caption: string;
  desktop: { w: number; h: number };
  mobile: { w: number; h: number };
  className: string;
  /** the hero only - everything else is below the fold */
  eager?: boolean;
  /**
   * Optional "read more" under the caption, for a shot that has a page of its own.
   * The tour is where most visitors meet a feature for the first time — the home page
   * carries the traffic and the bounce — so a shot whose subject has somewhere deeper
   * to go should say so here rather than making people find /features first. (#800)
   */
  link?: { to: string; label: string };
};

/* Two real crops, not one image squeezed twice. A 1900px desktop window scaled down to
   a 390px phone is an illegible smudge, and the CSS zoom that fixed the legibility cut
   the character card in half. So every shot ships a second file cropped to whatever its
   caption actually claims, and the browser picks: the phone gets the readable crop, the
   desktop keeps the whole clean window. Width and height on BOTH sources so neither one
   shifts the layout while it loads. (#770) */
export const ProductShot = ({ name, alt, caption, desktop, mobile, className, eager, link }: ProductShotProps) => (
  <figure className={className}>
    <picture>
      <source
        media="(max-width: 700px)"
        srcSet={`/product/${name}-mobile.png`}
        width={mobile.w}
        height={mobile.h}
      />
      <img
        src={`/product/${name}.png`}
        alt={alt}
        width={desktop.w}
        height={desktop.h}
        /* This is a CRA single-page app: the hero <img> does not exist until ~394KB of
           JS has parsed, so the preload scanner never sees it. eager alone buys nothing -
           the <link rel="preload"> in index.html is what actually starts the download,
           and this keeps it at the front of the queue once React mounts. */
        {...(eager ? { fetchPriority: 'high' as const } : { loading: 'lazy' as const })}
      />
    </picture>
    <figcaption>
      {caption}
      {/* Its own line rather than trailing the caption. The caption is a statement
          about the picture; this is an invitation to leave the page, and running them
          together made the sentence look like it had a footnote. */}
      {link && <Link className="tour-shot-link" to={link.to}>{link.label}</Link>}
    </figcaption>
  </figure>
);

const LandingPage = () => (
  <>
  <RouteSeo path="/" />
  <Helmet>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Chaptery",
      "url": "https://trychaptery.com",
      "applicationCategory": "WritingApplication",
      "operatingSystem": "macOS, Windows",
      "description": "A desktop writing app for novelists, with a manuscript editor, world building, a character relationship map, a storyboard, revision history and cloud sync in one application.",
      "offers": [
        { "@type": "Offer", "name": "Free Trial", "price": "0", "priceCurrency": "USD", "description": "Full access for one book, 30 days. No credit card required." },
        { "@type": "Offer", "name": "Cloud Monthly", "price": "9.99", "priceCurrency": "USD", "billingIncrement": "P1M", "description": "Full access, unlimited books, cancel anytime." },
        { "@type": "Offer", "name": "Cloud Annual", "price": "99.99", "priceCurrency": "USD", "billingIncrement": "P1Y", "description": "Full access, unlimited books, billed annually. Saves two months versus monthly." }
      ],
      "publisher": { "@type": "Organization", "name": "Chaptery", "url": "https://trychaptery.com" }
    })}</script>
  </Helmet>
  <div className="landing-page">
    <section className="hero-content">
      <div className="container">
        <h1>Write your novel. Keep your whole world straight.</h1>
        {/* One line, not five. Visitors spend 5s here and read 26% of the page, so the
            strongest claim has to be the first thing after the headline rather than
            buried mid-paragraph. It is also the framing of the best-performing ad
            headline we have. (#770) */}
        <p className="hero-subtitle">
          Your manuscript and your whole world in <strong>one app instead of five</strong>.
        </p>
        <div className="hero-actions">
          {/* Which button gets clicked is the point: hero means they decided from the
              headline, footer means they read the whole page first. Both land on the
              same route, so nothing else can tell them apart. (#767) */}
          <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('hero')}>Start Free</Link>
          {/* Demoted from a competing button to a quiet link: two equal-weight actions
              gave a five-second visitor a decision to make instead of a step to take. */}
          <Link to="/features" className="hero-secondary-link">See how it works →</Link>
        </div>
        {/* Every paid visitor so far arrived on a phone, and the editor is a desktop
            app - so this leads with what they can do on the device in their hand.
            Ending on "Windows & macOS" read as "not for you right now". (#770) */}
        <p className="hero-trial-note">
          30 days free · No credit card · Start in your browser now, write in the
          desktop app for macOS &amp; Windows
        </p>
        {/* The product, not a description of it. Every ad that brings people here is a
            screenshot; the page they landed on had none, which is a hard stop for a
            visual tool. The phone crop holds the manuscript beside both cards, so the
            caption below it is something the reader can check rather than take on
            trust. (#770) */}
        <ProductShot
          className="hero-shot"
          name="world-building"
          eager
          desktop={{ w: 1900, h: 1030 }}
          mobile={{ w: 1240, h: 702 }}
          alt="A chapter of a manuscript open beside character and location cards for the same story"
          caption="Your manuscript and your world, side by side."
        />
        <p className="hero-pricing-link">
          Already know what you need? <Link to="/pricing">View pricing →</Link>
        </p>
      </div>
    </section>

    <section className="features-showcase">
      <div className="container grid-3">
        <div className="feature-card">
          <div className="feature-icon-circle teal"><PenTool size={32} strokeWidth={2} /></div>
          <h3>Advanced Editing</h3>
          <p>A distraction-free writing environment with powerful structural tools to keep your draft organised from chapter one to the end.</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon-circle green"><Globe2 size={32} strokeWidth={2} /></div>
          <h3>World Building</h3>
          <p>Characters, locations, events, and a visual relationship map, all in one place so your world stays consistent across every chapter.</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon-circle orange"><Cloud size={32} strokeWidth={2} /></div>
          <h3>Desktop + Cloud</h3>
          <p>A fast native app for Mac and Windows, with automatic cloud sync so your manuscript and world notes are always backed up. Your shelf, world, and storyboard also open in any browser.</p>
        </div>
      </div>
      <div className="container" style={{ textAlign: 'center', marginTop: '32px' }}>
        <Link to="/features" className="landing-features-link">Explore all features →</Link>
      </div>
    </section>

    {/* The remaining screenshots, stacked rather than in a carousel. A carousel assumes
        dwell time this page measurably does not have - at five seconds the second slide
        arrives after the visitor has left, hiding three of four assets from nearly
        everyone. Stacked, a scroller sees all of them and nobody has to swipe. (#770) */}
    <section className="product-tour">
      <div className="container">
        <ProductShot
          className="tour-shot"
          name="storyboard"
          desktop={{ w: 1900, h: 1035 }}
          mobile={{ w: 1100, h: 745 }}
          alt="Storyboard cards describing story beats, each linked to the character it belongs to"
          caption="Plan the beats, and every card stays tied to who it belongs to."
        />
        {/* The only shot whose phone crop drops the manuscript pane, and it is the
            stronger image for it. A visible pane would prove a manuscript exists; the
            reply proves the assistant read it - "marine radio operator", "monitoring
            the coastline from an old lighthouse", "the strange signal echoing her
            name". That is the caption, and none of it is legible at 390px with the
            pane still in frame. Do not put the pane back. */}
        <ProductShot
          className="tour-shot"
          name="assistant"
          desktop={{ w: 3015, h: 1344 }}
          mobile={{ w: 610, h: 1134 }}
          alt="The Chaptery Assistant in the side panel next to the open manuscript, counting how often the author used &quot;said&quot; and its synonyms"
          caption="An assistant that can see your manuscript, not a blank chat box."
          link={{ to: '/features/assistant', label: 'See what you can ask it to do →' }}
        />
        <ProductShot
          className="tour-shot"
          name="writing-dashboard"
          desktop={{ w: 1058, h: 812 }}
          mobile={{ w: 1000, h: 438 }}
          alt="A writing dashboard showing words written today, a daily goal and an eleven day streak"
          caption="Words today, this week, this month, and the streak you are keeping."
        />
      </div>
    </section>

    <section className="cta-section">
      <div className="container">
        <h2>Ready to focus on your story?</h2>
        <p>Everything your story needs: manuscript editor, world builder, relationship map, and cloud sync, in one place.</p>
        <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('footer')}>Start Free</Link>
      </div>
    </section>
  </div>
  </>
);

export default LandingPage;
